/// <reference types="vite/client" />
// src/prototype/services/portfolioIntelligenceService.ts
// ============================================================
// Client for GET /api/v2/portfolio-intelligence/:clientId
// Maps raw API response to the Opportunity shape used by OIR section components.
// Supports both the legacy v1 `sections` envelope and the current v2 `data` envelope.
// ============================================================

import { sanitizeDisplayLabel } from '@/utils/sanitizeDisplayLabel';
import { dedupeGet, eaCaches, readTtl, writeTtl } from '../utils/sharedTtlCache';

const API_BASE = import.meta.env.VITE_API_URL || import.meta.env.VITE_BACKEND_URL || 'http://localhost:8080';

// ── V2 API response types ──────────────────────────────────────────────────

export interface PIV2UseCaseItem {
    // API returns numeric IDs in useCaseList but string IDs in top10UseCases / top3recommended
    use_case_id: number | string;
    name: string;
    category_name: string | null;
    category_id: number | null;
    annual_benefit_conservative: number | null;
    annual_benefit_base: number | null;
    annual_benefit_aggressive: number | null;
}

export interface PIV2TopRecommended extends PIV2UseCaseItem {
    payback_months_base: number;
}

export interface PIV2RoadmapItem {
    use_case_id: string | number;
    name: string;
    annual_benefit_conservative: number | null;
    annual_benefit_base: number | null;
    annual_benefit_aggressive: number | null;
}

export interface PIV2PersonalizationDimension {
    score: number;
    strength: string;
    sourceCount: number;
}

export interface PIV2PersonalizationLevel {
    organizationProfile: PIV2PersonalizationDimension;
    surveyResponses: PIV2PersonalizationDimension;
    uploadedDocuments: PIV2PersonalizationDimension;
    priorInitiatives: PIV2PersonalizationDimension;
    overallStrength: { score: number; strength: string };
}

export interface PIV2Data {
    annualValueLow: number;
    annualValueHigh: number;
    useCaseCount: number;
    useCaseList: PIV2UseCaseItem[];
    top10UseCases: PIV2UseCaseItem[];
    quickWinCount: number;
    top3recommended: PIV2TopRecommended[];
    personalizationLevel: PIV2PersonalizationLevel;
    knownConstraints?: string[];
    strategicPriorities?: string[];
    avgTimeToValue?: number;
    waterfall?: {
        baseline: number;
        benefits: number;
        costs: number;
        net: number;
        scenarios: {
            conservative: { benefits: number; costs: number; net: number };
            base: { benefits: number; costs: number; net: number };
            aggressive: { benefits: number; costs: number; net: number };
        };
    };
    executionRoadmap: {
        launch_now: PIV2RoadmapItem[];
        pilot: PIV2RoadmapItem[];
        monitor: PIV2RoadmapItem[];
        defer: PIV2RoadmapItem[];
    };
}

export interface PIV2Response {
    data: PIV2Data;
}

// ── Legacy v1 raw API response types ──────────────────────────────────────

// Used in executive_summary.top_recommended[]
export interface PITopRecommended {
    use_case_id: number;
    name: string;
    roi_low: number;
    roi_high: number;
    quick_win: boolean;
    execution_posture: 'launch_now' | 'pilot' | 'scale' | 'monitor' | 'defer';
    confidence_level: 'high' | 'medium' | 'low';
}

export interface PIExecutiveSummary {
    portfolio_value_low: number;
    portfolio_value_high: number;
    use_case_count: number;
    quick_win_count: number;
    routine_initiative_count: number;
    top_10_value_low: number;
    top_10_value_high: number;
    top_recommended: PITopRecommended[];
}

export interface PIStrategicContext {
    organization_name: string;
    organization_type_id: number | null;
    personalization_score: number;
    personalization_level: 'high' | 'medium' | 'low';
    executive_summary_text: string | null;
    strategic_priorities: string[];
    key_constraints: string[];
    strategic_themes: string[];
    recommended_next_steps: string[];
    // V2 extended: individual dimension scores from personalizationLevel
    personalization_profile_score?: number;
    personalization_surveys_score?: number;
    personalization_documents_score?: number;
    personalization_initiatives_score?: number;
}

export interface PIDomain {
    name: string;
    use_case_count: number;
    use_case_ids: number[];
}

export interface PIOpportunityLandscape {
    strategic_domain_count: number;
    total_use_cases: number;
    largest_domain: string;
    domains: PIDomain[];
    strategic_themes: string[];
}

// Used in financial_impact.initiatives[]
export interface PIFinancialInitiative {
    use_case_id: number;
    name: string;
    roi_low: number;
    roi_high: number;
    annual_benefit: number;
    annual_cost: number;
    roi_ratio: number;
}

export interface PIWaterfallScenario {
    benefits: number;
    costs: number;
    net: number;
}

export interface PIFinancialImpact {
    portfolio_value_low: number;
    portfolio_value_high: number;
    top_10_value_low: number;
    top_10_value_high: number;
    estimated_payback_months: number | null;
    avg_time_to_value?: number;
    waterfall: {
        conservative: PIWaterfallScenario;
        base: PIWaterfallScenario;
        aggressive: PIWaterfallScenario;
    };
    initiatives: PIFinancialInitiative[];
}

// Used in decision_canvas.initiatives[]
export interface PICanvasInitiative {
    use_case_id: number;
    name: string;
    rank: number;
    annual_value_low: number;
    annual_value_base: number;
    annual_value_high: number;
    quick_win: boolean;
    execution_posture: 'launch_now' | 'pilot' | 'scale' | 'monitor' | 'defer';
    value_score: number;
    implementation_score: number;
    confidence_level: 'high' | 'medium' | 'low';
    category?: string;
    payback_months?: number;
}

export interface PIDecisionCanvas {
    initiatives: PICanvasInitiative[];
    posture_summary: {
        launch_now: number;
        pilot: number;
        scale: number;
        defer: number;
    };
    value_by_category: Array<{
        category: string;
        total_value: number;
        use_case_count: number;
    }>;
}

export interface PIMilestone {
    label: string;
    months_from_start: number;
    value_note: string;
}

export interface PIRoadmapInitiative {
    use_case_id: number;
    name: string;
    category: string;
    rank: number;
    quick_win: boolean;
    annual_value_low: number;
    annual_value_high: number;
    milestones: PIMilestone[];
}

export interface PIPhase {
    label: string;
    description: string;
    count: number;
    initiatives: PIRoadmapInitiative[];
}

export interface PIRoadmap {
    phases: {
        launch_now: PIPhase;
        pilot: PIPhase;
        scale: PIPhase;
        defer: PIPhase;
    };
}

export interface PIReport {
    success: boolean;
    client_id: string | number;
    generated_at: string;
    meta: {
        portfolio_size: number;
        portfolio_filter: number[] | null;
    };
    sections: {
        executive_summary: PIExecutiveSummary;
        strategic_context: PIStrategicContext;
        opportunity_landscape: PIOpportunityLandscape;
        financial_impact: PIFinancialImpact;
        decision_canvas: PIDecisionCanvas;
        roadmap: PIRoadmap;
    };
}

// ── Simplified Opportunity shape used by OIR section components ───────────
// Matches the structural minimum required by OIRReportView and its section components.

export interface ReportOpportunity {
    id: string;
    rank: number;
    title: string;
    category: string;
    implementationEffort: 'Low' | 'Medium' | 'High';
    timeToValue: string;
    /** conservative / base / aggressive annual values in $M */
    roiEstimate: { low: number; base?: number; high: number };
    confidence: string;
    /** UCE list `value_band` — same badge Use Case Explorer shows when present. */
    valueBand?: string;
    bv_axis?: number;
    ie_axis?: number;
}

// ── Helpers ────────────────────────────────────────────────────────────────

const RAW_TO_MILLIONS = (raw: number): number => {
    // API returns raw dollars (e.g. 81_000_000). Convert to millions.
    if (Math.abs(raw) >= 1_000) return raw / 1_000_000;
    // Already in millions (legacy/test data)
    return raw;
};

/**
 * Resolve conservative / base / aggressive into an ordered low–high band.
 * Matches Use Case Explorer: fall back across available benefit fields, then min/max.
 * Accepts raw dollars or already-$M values (callers convert consistently).
 */
function resolveRoiBand(
    low: number | null | undefined,
    base: number | null | undefined,
    high: number | null | undefined,
): { low: number; base: number; high: number } {
    const resolvedBase = base || low || high || 0;
    const cons = low || resolvedBase;
    const aggr = high || resolvedBase;
    const bandLow = Math.min(cons, aggr);
    const bandHigh = Math.max(cons, aggr);
    const bandBase = base || ((bandLow + bandHigh) / 2) || 0;
    return { low: bandLow, base: bandBase, high: bandHigh };
}

export function formatMoney(value: number | null | undefined): string {
    if (value == null) return 'N/A';
    if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(1)}B`;
    if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
    if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
    return `$${value.toLocaleString()}`;
}

const POSTURE_TO_EFFORT: Record<string, ReportOpportunity['implementationEffort']> = {
    launch_now: 'Low',
    // BQ "scale" is Evaluate "monitor" — treat as moderate watch posture, not low-effort Scale.
    scale: 'Medium',
    monitor: 'Medium',
    pilot: 'Medium',
    defer: 'High',
};

const paybackToTimeToValue = (months: number | null | undefined): string => {
    if (!months) return '6-12 months';
    if (months <= 6) return '3-6 months';
    if (months <= 12) return '6-12 months';
    if (months <= 18) return '12-18 months';
    return '18+ months';
};

// ── V2 → PIReport transformer ──────────────────────────────────────────────

function transformV2ToReport(v2: PIV2Data, clientId: string): PIReport {
    const { useCaseList, top10UseCases, top3recommended, executionRoadmap, personalizationLevel } = v2;

    // Build posture lookup: use_case_id → execution_posture
    // Always normalise IDs to Number to avoid string/number key mismatches.
    // Priority order: executionRoadmap buckets first, then top3recommended as launch_now fallback.
    // DMD-1201: keep Evaluate taxonomy (monitor), not the legacy BQ "scale" label.
    const postureMap = new Map<number, 'launch_now' | 'pilot' | 'scale' | 'monitor' | 'defer'>();
    (executionRoadmap.launch_now ?? []).forEach(i => postureMap.set(Number(i.use_case_id), 'launch_now'));
    (executionRoadmap.pilot ?? []).forEach(i => postureMap.set(Number(i.use_case_id), 'pilot'));
    (executionRoadmap.monitor ?? []).forEach(i => postureMap.set(Number(i.use_case_id), 'monitor'));
    (executionRoadmap.defer ?? []).forEach(i => postureMap.set(Number(i.use_case_id), 'defer'));
    // top3recommended are always treated as launch_now (short payback; may not appear in executionRoadmap).
    // use_case_id here comes as a string from the API ("13"), so Number() is required.
    top3recommended.forEach(r => {
        if (!postureMap.has(Number(r.use_case_id))) postureMap.set(Number(r.use_case_id), 'launch_now');
    });

    // Build a set of all use_case_ids present in useCaseList for fast lookup (normalised to number)
    const useCaseListIds = new Set(useCaseList.map(u => Number(u.use_case_id)));

    // Group useCaseList by category for the opportunity landscape
    const categoryMap = new Map<string, { ids: number[] }>();
    useCaseList.forEach(uc => {
        const cat = uc.category_name ?? 'Uncategorized';
        if (!categoryMap.has(cat)) categoryMap.set(cat, { ids: [] });
        categoryMap.get(cat)!.ids.push(Number(uc.use_case_id));
    });
    const domains: PIDomain[] = Array.from(categoryMap.entries()).map(([name, val]) => ({
        name,
        use_case_count: val.ids.length,
        use_case_ids: val.ids,
    }));
    const largestDomain = domains.reduce((best, d) => d.use_case_count > best.use_case_count ? d : best, domains[0] ?? { name: '', use_case_count: 0, use_case_ids: [] });

    // Top 10 value sums (only non-null items)
    const top10ValueLow = top10UseCases.reduce((s, u) => s + (u.annual_benefit_conservative ?? 0), 0);
    const top10ValueHigh = top10UseCases.reduce((s, u) => s + (u.annual_benefit_aggressive ?? 0), 0);

    // Map top3recommended → PITopRecommended
    const topRecommended: PITopRecommended[] = top3recommended.map(r => ({
        use_case_id: Number(r.use_case_id),
        name: sanitizeDisplayLabel(r.name) || r.name,
        roi_low: r.annual_benefit_conservative ?? 0,
        roi_high: r.annual_benefit_aggressive ?? 0,
        quick_win: (r.payback_months_base ?? 12) <= 6,
        execution_posture: postureMap.get(Number(r.use_case_id)) ?? 'launch_now',
        confidence_level: 'medium',
    }));

    // Collect any executionRoadmap items not present in useCaseList (e.g. test items like ID 9999)
    // IDs in executionRoadmap items not present in useCaseList (e.g. test item ID 9999)
    const allRoadmapItems: PIV2RoadmapItem[] = [
        ...(executionRoadmap.launch_now ?? []),
        ...(executionRoadmap.pilot ?? []),
        ...(executionRoadmap.monitor ?? []),
        ...(executionRoadmap.defer ?? []),
    ].filter(i => !useCaseListIds.has(Number(i.use_case_id)));  // useCaseListIds is already Number-keyed

    // Map useCaseList → PICanvasInitiative (ranked: items with benefit data first, sorted by base value)
    const sortedUseCases = [...useCaseList].sort((a, b) => {
        const aVal = a.annual_benefit_base ?? ((a.annual_benefit_conservative ?? 0) + (a.annual_benefit_aggressive ?? 0)) / 2;
        const bVal = b.annual_benefit_base ?? ((b.annual_benefit_conservative ?? 0) + (b.annual_benefit_aggressive ?? 0)) / 2;
        return bVal - aVal;
    });

    // Build payback lookup from top3recommended so initiatives carry the real payback duration
    const paybackMap = new Map<number, number>();
    top3recommended.forEach(r => {
        if (r.payback_months_base != null) paybackMap.set(Number(r.use_case_id), r.payback_months_base);
    });

    const toCanvasInitiative = (
        id: number,
        name: string,
        category: string | null,
        low: number | null,
        base: number | null,
        high: number | null,
        rank: number,
    ): PICanvasInitiative => {
        const posture = postureMap.get(id) ?? 'defer';
        const band = resolveRoiBand(low, base, high);
        const hasValue = band.base > 0 || band.low > 0;
        return {
            use_case_id: id,
            name: sanitizeDisplayLabel(name) || name,
            rank,
            annual_value_low: band.low,
            annual_value_base: band.base,
            annual_value_high: band.high,
            quick_win: posture === 'launch_now' && hasValue,
            execution_posture: posture,
            value_score: hasValue ? 75 : 40,
            implementation_score: posture === 'launch_now' ? 80 : 50,
            confidence_level: hasValue ? 'high' : 'medium',
            category: sanitizeDisplayLabel(category) || category || 'General',
            payback_months: paybackMap.get(id),
        };
    };

    const canvasFromList: PICanvasInitiative[] = sortedUseCases.map((uc, idx) =>
        toCanvasInitiative(Number(uc.use_case_id), uc.name, uc.category_name, uc.annual_benefit_conservative, uc.annual_benefit_base, uc.annual_benefit_aggressive, idx + 1)
    );

    // Extra roadmap items (not in useCaseList) are inserted at the front with low ranks
    // so that getBucket() correctly classifies them by their execution_posture.
    const extraCanvasItems: PICanvasInitiative[] = allRoadmapItems.map((item, idx) =>
        toCanvasInitiative(Number(item.use_case_id), item.name, null, item.annual_benefit_conservative, item.annual_benefit_base, item.annual_benefit_aggressive, idx + 1)
    );

    // Re-rank canvasFromList after extra items
    const rerankedFromList = canvasFromList.map((c, idx) => ({ ...c, rank: extraCanvasItems.length + idx + 1 }));

    const canvasInitiatives: PICanvasInitiative[] = [...extraCanvasItems, ...rerankedFromList];

    // Value by category (from useCaseList)
    const valueByCat: Array<{ category: string; total_value: number; use_case_count: number }> = Array.from(categoryMap.entries()).map(([cat, val]) => {
        const items = useCaseList.filter(u => (u.category_name ?? 'Uncategorized') === cat);
        const total = items.reduce((s, u) => s + (u.annual_benefit_base ?? u.annual_benefit_conservative ?? 0), 0);
        return { category: cat, total_value: total, use_case_count: val.ids.length };
    });

    // Posture summary counts (scale counted under monitor for Evaluate parity — DMD-1201)
    const postureSummary = { launch_now: 0, pilot: 0, scale: 0, defer: 0 };
    canvasInitiatives.forEach(i => {
        const key = i.execution_posture === 'monitor' ? 'scale' : i.execution_posture;
        if (key in postureSummary) postureSummary[key as keyof typeof postureSummary]++;
    });

    // Map executionRoadmap → PIRoadmap phases
    const mapToPhase = (items: PIV2RoadmapItem[], label: string, description: string): PIPhase => ({
        label,
        description,
        count: items.length,
        initiatives: items.map((item, idx) => {
            const band = resolveRoiBand(
                item.annual_benefit_conservative,
                item.annual_benefit_base,
                item.annual_benefit_aggressive,
            );
            return {
                use_case_id: Number(item.use_case_id),
                name: item.name,
                category: useCaseList.find(u => Number(u.use_case_id) === Number(item.use_case_id))?.category_name ?? 'General',
                rank: idx + 1,
                quick_win: false,
                annual_value_low: band.low,
                annual_value_high: band.high,
                milestones: [],
            };
        }),
    });

    // Personalization strength → level
    const strengthToLevel = (s: string): 'high' | 'medium' | 'low' =>
        s === 'strong' ? 'high' : s === 'moderate' ? 'medium' : 'low';

    // Waterfall: always roll up from top10UseCases (matches UCE / FI top-10).
    // API waterfall / annualValue* can be full-portfolio inflated figures — only reuse costs when scaled.
    const top10ConsSum = top10UseCases.reduce((s, u) => s + (u.annual_benefit_conservative ?? 0), 0);
    const top10BaseSum = top10UseCases.reduce((s, u) => s + (u.annual_benefit_base ?? u.annual_benefit_conservative ?? 0), 0);
    const top10AggrSum = top10UseCases.reduce((s, u) => s + (u.annual_benefit_aggressive ?? 0), 0);
    const consM = RAW_TO_MILLIONS(top10ConsSum);
    const baseM = RAW_TO_MILLIONS(top10BaseSum);
    const aggrM = RAW_TO_MILLIONS(top10AggrSum);

    const apiWf = v2.waterfall?.scenarios;
    const apiBaseBenefitsM = apiWf ? RAW_TO_MILLIONS(apiWf.base.benefits) : 0;
    const apiCostsM = apiWf ? RAW_TO_MILLIONS(apiWf.base.costs) : 0;
    const costScale =
      apiBaseBenefitsM > 0 && apiBaseBenefitsM > baseM * 1.5
        ? baseM / apiBaseBenefitsM
        : apiWf ? 1 : 0;
    const costsM = Math.max(0, apiCostsM * costScale);

    const waterfall = {
        conservative: { benefits: consM, costs: costsM, net: consM - costsM },
        base:         { benefits: baseM, costs: costsM, net: baseM - costsM },
        aggressive:   { benefits: aggrM, costs: costsM, net: aggrM - costsM },
    };

    return {
        success: true,
        client_id: clientId,
        generated_at: new Date().toISOString(),
        meta: {
            portfolio_size: v2.useCaseCount,
            portfolio_filter: null,
        },
        sections: {
            executive_summary: {
                portfolio_value_low: v2.annualValueLow,
                portfolio_value_high: v2.annualValueHigh,
                use_case_count: v2.useCaseCount,
                quick_win_count: v2.quickWinCount,
                routine_initiative_count: v2.useCaseCount - v2.quickWinCount,
                top_10_value_low: top10ValueLow,
                top_10_value_high: top10ValueHigh,
                top_recommended: topRecommended,
            },
            strategic_context: {
                organization_name: '',
                organization_type_id: null,
                personalization_score: personalizationLevel.overallStrength.score,
                personalization_level: strengthToLevel(personalizationLevel.overallStrength.strength),
                executive_summary_text: null,
                strategic_priorities: v2.strategicPriorities ?? [],
                key_constraints: v2.knownConstraints ?? [],
                strategic_themes: [],
                recommended_next_steps: [],
                personalization_profile_score: personalizationLevel.organizationProfile.score,
                personalization_surveys_score: personalizationLevel.surveyResponses.score,
                personalization_documents_score: personalizationLevel.uploadedDocuments.score,
                personalization_initiatives_score: personalizationLevel.priorInitiatives.score,
            },
            opportunity_landscape: {
                strategic_domain_count: domains.length,
                total_use_cases: v2.useCaseCount,
                largest_domain: largestDomain.name,
                domains,
                strategic_themes: [],
            },
            financial_impact: {
                portfolio_value_low: v2.annualValueLow,
                portfolio_value_high: v2.annualValueHigh,
                top_10_value_low: top10ValueLow,
                top_10_value_high: top10ValueHigh,
                estimated_payback_months: top3recommended[0]?.payback_months_base ?? null,
                avg_time_to_value: v2.avgTimeToValue,
                waterfall,
                initiatives: top10UseCases.map(uc => ({
                    use_case_id: Number(uc.use_case_id),
                    name: uc.name,
                    roi_low: RAW_TO_MILLIONS(uc.annual_benefit_conservative ?? 0),
                    roi_high: RAW_TO_MILLIONS(uc.annual_benefit_aggressive ?? 0),
                    annual_benefit: RAW_TO_MILLIONS(uc.annual_benefit_base ?? (((uc.annual_benefit_conservative ?? 0) + (uc.annual_benefit_aggressive ?? 0)) / 2)),
                    annual_cost: 0,
                    roi_ratio: 0,
                })),
            },
            decision_canvas: {
                initiatives: canvasInitiatives,
                posture_summary: postureSummary,
                value_by_category: valueByCat,
            },
            roadmap: {
                phases: {
                    launch_now: mapToPhase(executionRoadmap.launch_now ?? [], 'Launch Now', 'High-value, high-readiness initiatives to start immediately'),
                    pilot: mapToPhase(executionRoadmap.pilot ?? [], 'Pilot', 'Promising initiatives requiring validation before full rollout'),
                    // Label Monitor (not Scale) so Evaluate / Execute posture names match — DMD-1201.
                    scale: mapToPhase(executionRoadmap.monitor ?? [], 'Monitor', 'Watch and assess before committing'),
                    defer: mapToPhase(executionRoadmap.defer ?? [], 'Defer', 'Initiatives to revisit once foundational capabilities are in place'),
                },
            },
        },
    };
}

// ── Mapper: canvas initiatives → OIRReportView Opportunity shape ───────────

export function mapReportToOpportunities(report: PIReport): ReportOpportunity[] {
    const canvas = report.sections.decision_canvas;
    const initiatives = canvas?.initiatives ?? [];

    return initiatives.map((init, idx): ReportOpportunity => {
        // Convert first, then resolve band so null cons/aggr fall back to base in $M space
        const band = resolveRoiBand(
            init.annual_value_low != null ? RAW_TO_MILLIONS(init.annual_value_low) : null,
            init.annual_value_base != null ? RAW_TO_MILLIONS(init.annual_value_base) : null,
            init.annual_value_high != null ? RAW_TO_MILLIONS(init.annual_value_high) : null,
        );
        return {
            id: String(init.use_case_id),
            rank: init.rank ?? idx + 1,
            title: sanitizeDisplayLabel(init.name) || init.name,
            category: sanitizeDisplayLabel(init.category) || init.category || 'General',
            implementationEffort: POSTURE_TO_EFFORT[init.execution_posture ?? ''] ?? 'Medium',
            timeToValue: paybackToTimeToValue(init.payback_months ?? null),
            roiEstimate: { low: band.low, base: band.base || undefined, high: band.high },
            confidence: init.confidence_level ?? 'Medium',
        };
    });
}

export function formatPortfolioRange(low: number | null | undefined, high: number | null | undefined): string {
    if (low == null || high == null) return 'N/A';
    const toM = RAW_TO_MILLIONS;
    const fmt = (v: number) => v < 10 ? v.toFixed(1) : Math.round(v).toString();
    return `$${fmt(toM(low))}M – $${fmt(toM(high))}M`;
}

// ── API fetch ──────────────────────────────────────────────────────────────

const PI_TTL_MS = 60_000;

export async function fetchPortfolioIntelligenceReport(clientId: string, portfolioIds?: number[]): Promise<PIReport> {
    const qs = portfolioIds && portfolioIds.length > 0 ? `?portfolio=${portfolioIds.join(',')}` : '';
    const cacheKey = `${clientId}${qs}`;
    const cached = readTtl<PIReport>(eaCaches().pi, cacheKey, PI_TTL_MS);
    if (cached) return cached;

    return dedupeGet(`pi:${cacheKey}`, async () => {
        const response = await fetch(
            `${API_BASE}/api/v2/portfolio-intelligence/${encodeURIComponent(clientId)}${qs}`,
            {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
            }
        );

        if (!response.ok) {
            throw new Error(`Portfolio Intelligence API error: ${response.status} ${response.statusText}`);
        }

        const raw = await response.json();

        // V2 response: { data: { annualValueLow, useCaseList, ... } }
        let report: PIReport;
        if (raw && typeof raw === 'object' && 'data' in raw && raw.data && 'annualValueLow' in raw.data) {
            report = transformV2ToReport(raw.data as PIV2Data, clientId);
        } else {
            // Legacy V1 response: { success, sections: { ... } }
            const data = raw as PIReport;
            if (!data.success) {
                throw new Error((data as unknown as { error?: string }).error ?? 'API returned success: false');
            }
            report = data;
        }
        writeTtl(eaCaches().pi, cacheKey, report);
        return report;
    });
}
